import { FieldPath, FieldValues, get, useController } from 'react-hook-form';
import { FormField, FormFieldProps } from '@britishcouncil/react-admin';
import { YearMonthPicker, YearMonthPickerProps, getDisplayValue } from '../year-month-picker';
import { useFormContext } from './FormContext';
import { RHFormFieldProps } from './types';
import { ReadonlyFormValue } from './ReadonlyFormValue';

type RHFormYearMonthPickerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = RHFormFieldProps<FormFieldProps, TFieldValues, TName> &
  Omit<YearMonthPickerProps, 'onChange' | 'value'> & {
    readonlyMode?: boolean;
  };

export const RHFormYearMonthPicker = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  required,
  disabled,
  rules,
  defaultValue,
  shouldUnregister,
  readonlyMode,
  ...props
}: RHFormYearMonthPickerProps<TFieldValues, TName>) => {
  const { mode: formMode } = useFormContext();
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  if (formMode === 'read' || readonlyMode) {
    return (
      <ReadonlyFormValue
        label={props.label}
        value={field.value}
        renderValue={(value) => {
          if (!value) return '-';
          return getDisplayValue(value, props.mode);
        }}
      />
    );
  }

  return (
    <FormField
      required={required}
      disabled={disabled}
      control={YearMonthPicker}
      touched={true}
      error={get(formState.errors, name)?.message}
      {...field}
      {...props}
    />
  );
};
